import { readEnvValueWithFallback, envValidators } from "@/lib/env";

/**
 * Centralized contact email addresses.
 * Change here to update across the entire site.
 *
 * SC-5 (functional audit 2026-06-27): Privacy Policy / AUP / Legal landing
 * previously surfaced an `info@` address that is not in active use, while the
 * rest of the site routes external contact through `sales@` (commercial) and
 * `noc@` (incident response). To present a single inbound channel for general
 * external contact, `info` now defaults to `sales@sofia-connect.net`. The
 * `info` key is retained so existing import sites (legal pages, mailto links)
 * keep working without change, and operators can still override to a true
 * info@ mailbox later via NEXT_PUBLIC_INFO_EMAIL if one is provisioned.
 *
 * All reads go through readEnvWithFallback so a trailing newline or
 * interior whitespace surfaces as a hard error naming the variable
 * (see src/lib/env.ts). Silent tolerance of malformed mailto: values
 * had a real cost — some email clients open them, some refuse
 * outright, and neither behaviour points at the root cause.
 */
// NEXT_PUBLIC_* reads MUST be written as the literal expression
// `process.env.NEXT_PUBLIC_X` so Next inlines the build-time value
// into the client bundle. `process.env[dynamicName]` is stripped
// and evaluates to undefined in the browser — see src/lib/env.ts.
export const CONTACT_EMAIL = {
  sales: readEnvValueWithFallback("NEXT_PUBLIC_SALES_EMAIL", process.env.NEXT_PUBLIC_SALES_EMAIL, "sales@sofia-connect.net", { validate: envValidators.email }),
  noc: readEnvValueWithFallback("NEXT_PUBLIC_NOC_EMAIL", process.env.NEXT_PUBLIC_NOC_EMAIL, "noc@sofia-connect.net", { validate: envValidators.email }),
  // Intentionally falls back to sales@ — see block comment above for SC-5 fix.
  info: readEnvValueWithFallback("NEXT_PUBLIC_INFO_EMAIL", process.env.NEXT_PUBLIC_INFO_EMAIL, "sales@sofia-connect.net", { validate: envValidators.email }),
  peering: readEnvValueWithFallback("NEXT_PUBLIC_PEERING_EMAIL", process.env.NEXT_PUBLIC_PEERING_EMAIL, "peering@sofia-connect.net", { validate: envValidators.email }),
} as const;

/** Contact form recipient — server-side env, so plain readEnvValueWithFallback
 *  with a server-side literal read is safe here too. */
export const CONTACT_FORM_TO = readEnvValueWithFallback(
  "CONTACT_FORM_TO",
  process.env.CONTACT_FORM_TO,
  "nushev@gmail.com",
  { validate: envValidators.email },
);

/**
 * Central telephone number for the Sofia Connect sales and NOC desk.
 * Kept in E.164 (no spaces or punctuation) so it can be dropped into
 * schema.org ContactPoint and tel: links without further formatting.
 *
 * Change once here — src/app/layout.tsx Organization ContactPoints and
 * per-PoP LocalBusiness nodes in src/lib/network-schema.ts read from
 * this constant.
 */
export const CONTACT_PHONE_E164 = "+35924200300" as const;

